// src/components/PaymentInterface.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { ArrowBack, Payment, CreditCard, AccountBalance, LocalAtm, QrCode } from '@mui/icons-material';
import { APP_CONFIG } from '../constants/appConstants';
import { postSale, ApiPaymentMethod, SaleItem } from '../services/SaleServices';
import { CartItem } from '../types/CartItem';
import { priceCart, PriceCartResponse } from '../services/pricingService';
import { productService } from '../services/productService';
import type { ReceiptPayload } from '../types/ReceiptTypes';

interface PaymentInterfaceProps {
  totalAmount: number;
  onPaymentComplete: (receipt: ReceiptPayload) => void; // ✅ changed
  onBackToCart: () => void;

  cartItems?: CartItem[];
  posId?: string;
  locationId?: number;
  transactionNumber?: string;
}

type PaymentMethod = 'card' | 'debit' | 'cash' | 'student_card' | 'gift_card';

const PaymentInterface: React.FC<PaymentInterfaceProps> = ({
  totalAmount,
  onPaymentComplete,
  onBackToCart,
  cartItems = [],
  posId,
  locationId,
  transactionNumber,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [studentId, setStudentId] = useState('');
  const [giftCardNumber, setGiftCardNumber] = useState('');
  const [giftCardPin, setGiftCardPin] = useState('');

  // Promo + pricing
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoSuccess, setPromoSuccess] = useState<string | null>(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const [autoPricing, setAutoPricing] = useState<PriceCartResponse | null>(null);
  const [manualPricing, setManualPricing] = useState<PriceCartResponse | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);

  const [productIndex, setProductIndex] = useState<Map<string, any> | null>(null);

  const formatPrice = (price: number) => `$${price.toFixed(2)}`;

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const products = await productService.getAll();
        const idx = new Map<string, any>();
        products.forEach((p) => {
          if (p.barcode) idx.set(p.barcode, p);
          if (p.isbn) idx.set(p.isbn, p);
        });
        if (alive) setProductIndex(idx);
      } catch {
        if (alive) setProductIndex(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setPricingError(null);
    setManualPricing(null); // reset manual when cart changes

    if (!cartItems?.length) {
      setAutoPricing(null);
      return;
    }

    (async () => {
      try {
        const res = await priceCart({ cartItems, locationId, channel: 'POS' });
        if (alive) setAutoPricing(res);
      } catch (e: any) {
        if (alive) {
          setAutoPricing(null);
          setPricingError(e?.message || 'Failed to calculate pricing.');
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [cartItems, locationId]);

  const activePricing = manualPricing ?? autoPricing;

  const pricingByUpc = useMemo(() => {
    const m = new Map<
      string,
      { line_subtotal: number; line_discount: number; applied_promos: string[] }
    >();
    (activePricing?.items || []).forEach((i) => {
      m.set(i.item_sku, {
        line_subtotal: i.line_subtotal ?? 0,
        line_discount: i.line_discount ?? 0,
        applied_promos: Array.isArray(i.applied_promos) ? i.applied_promos : [],
      });
    });
    return m;
  }, [activePricing]);

  const appliedPromos = useMemo(() => {
    const s = new Set<string>();
    (activePricing?.items || []).forEach((i) =>
      (i.applied_promos || []).forEach((p) => s.add(String(p).toUpperCase()))
    );
    return Array.from(s);
  }, [activePricing]);

  const alreadyAppliedPromoSet = useMemo(() => {
    const s = new Set<string>();
    (autoPricing?.items || []).forEach((i) =>
      (i.applied_promos || []).forEach((p) => s.add(String(p).toUpperCase()))
    );
    (manualPricing?.items || []).forEach((i) =>
      (i.applied_promos || []).forEach((p) => s.add(String(p).toUpperCase()))
    );
    return s;
  }, [autoPricing, manualPricing]);

  const totals = useMemo(() => {
    const subtotalBefore = activePricing?.totals?.subtotal_before_discount ?? totalAmount;
    const discount = activePricing?.totals?.total_discount ?? 0;
    const subtotalAfter = activePricing?.totals?.subtotal_after_discount ?? Math.max(0, subtotalBefore - discount);

    let bottleDepositTotal = 0;
    let taxTotal = 0;

    for (const ci of cartItems || []) {
      const upc = ci.barcode?.trim() || ci.isbn?.trim() || '';
      const qty = Math.max(1, ci.quantity || 1);

      const meta = productIndex?.get(upc);

      const depositPer = Number(meta?.bottleDeposit ?? ci.bottleDeposit ?? 0);
      const depositApplicable = Boolean(meta?.bottleDepositApplicable ?? ci.bottleDepositApplicable);
      if (depositApplicable && depositPer > 0) bottleDepositTotal += depositPer * qty;

      const taxApplicable = Boolean(meta?.taxApplicable ?? ci.taxApplicable);
      const taxRate = Number(meta?.taxRate ?? ci.taxRate ?? 0);

      if (taxApplicable && taxRate > 0) {
        const line = pricingByUpc.get(upc);
        const base = Math.max(0, (line?.line_subtotal ?? (ci.price * qty)) - (line?.line_discount ?? 0));
        taxTotal += base * taxRate;
      }
    }

    const grandTotal = subtotalAfter + bottleDepositTotal + taxTotal;

    return {
      subtotalBefore,
      discount,
      subtotalAfter,
      bottleDepositTotal,
      taxTotal,
      grandTotal,
    };
  }, [activePricing, cartItems, pricingByUpc, productIndex, totalAmount]);

  const validatePaymentForm = () => {
    if (APP_CONFIG.TESTING_MODE) return true;

    switch (paymentMethod) {
      case 'card':
        if (!cardNumber || cardNumber.length < 16) {
          setError('Please enter a valid card number (16 digits)');
          return false;
        }
        if (!expiryDate || expiryDate.length < 5) {
          setError('Please enter a valid expiry date (MM/YY)');
          return false;
        }
        if (!cvv || cvv.length < 3) {
          setError('Please enter a valid CVV (3-4 digits)');
          return false;
        }
        break;
      case 'debit':
        if (!cardNumber || cardNumber.length < 16) {
          setError('Please enter a valid debit card number (16 digits)');
          return false;
        }
        if (!cvv || cvv.length < 4) {
          setError('Please enter a valid PIN (4 digits)');
          return false;
        }
        break;
      case 'student_card':
        if (!studentId || studentId.length < 5) {
          setError('Please enter a valid student ID');
          return false;
        }
        break;
      case 'gift_card':
        if (!giftCardNumber || giftCardNumber.length < 8) {
          setError('Please enter a valid gift card number');
          return false;
        }
        if (!giftCardPin || giftCardPin.length < 4) {
          setError('Please enter a valid gift card PIN');
          return false;
        }
        break;
      case 'cash':
        break;
    }
    return true;
  };

  const mapPaymentMethod = (m: PaymentMethod): ApiPaymentMethod => {
    switch (m) {
      case 'card':
        return 'CARD';
      case 'debit':
        return 'DEBIT';
      case 'cash':
        return 'CASH';
      case 'student_card':
        return 'STUDENT_CARD';
      case 'gift_card':
        return 'GIFT_CARD';
      default:
        return 'CARD';
    }
  };

  const makeTxnNumber = (): string => {
    const t = new Date();
    const pad = (n: number, w = 2) => n.toString().padStart(w, '0');
    const serial = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${t.getFullYear()}${pad(t.getMonth() + 1)}${pad(t.getDate())}${pad(t.getHours())}${pad(t.getMinutes())}${pad(t.getSeconds())}-${serial}`;
  };

  const makeReceiptNumber = (): string => `RCP-${Date.now().toString().slice(-8)}`;

  const toSaleItems = (items: CartItem[] = []): SaleItem[] => {
    return items
      .map((ci) => {
        const sku = ci.barcode?.trim() || ci.isbn?.trim() || '';
        if (!sku) return null;
        return {
          item_sku: sku,
          qty: Math.max(1, ci.quantity || 1),
          unit_price: typeof ci.price === 'number' ? Number(ci.price) : undefined,
        };
      })
      .filter(Boolean) as SaleItem[];
  };

  // ✅ Apply promo: block if already applied via auto/manual pricing
  const handleApplyPromo = async () => {
    setPromoError(null);
    setPromoSuccess(null);

    const code = promoCode.trim().toUpperCase();
    if (!code) {
      setPromoError('Please enter a promo code.');
      return;
    }
    if (!cartItems.length) {
      setPromoError('No items in cart to apply promo.');
      return;
    }
    if (alreadyAppliedPromoSet.has(code)) {
      setPromoError('Discount already applied.');
      return;
    }

    setPromoLoading(true);
    try {
      const beforeDiscount = autoPricing?.totals?.total_discount ?? 0;

      const res = await priceCart({
        cartItems,
        locationId,
        channel: 'POS',
        manualPromoCodes: [code],
      });

      const afterDiscount = res?.totals?.total_discount ?? 0;

      if (afterDiscount <= beforeDiscount) {
        setManualPricing(null);
        setPromoError('Invalid or ineligible promo code for this order.');
      } else {
        setManualPricing(res);
        setPromoSuccess(`Promo applied. You saved ${formatPrice(afterDiscount - beforeDiscount)}.`);
      }
    } catch (e: any) {
      setManualPricing(null);
      setPromoError(e?.message || 'Failed to apply promo code.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handlePayment = async () => {
    setIsProcessing(true);
    setError(null);

    if (!validatePaymentForm()) {
      setIsProcessing(false);
      return;
    }

    try {
      // (your simulation retained)
      await new Promise((resolve) => setTimeout(resolve, 1200));
      const success = true;

      if (success) {
        // Build payload for backend sale registration
        const items = toSaleItems(cartItems);
        const payload = {
          location_id: typeof locationId === 'number' ? locationId : (APP_CONFIG.LOCATION_ID ?? 0),
          pos_id: posId || (APP_CONFIG.POS_NUMBER ?? 'UNKNOWN_POS'),
          transaction_number: transactionNumber || makeTxnNumber(),
          payment_method: mapPaymentMethod(paymentMethod),
          items,
          tax_amount: totals.taxTotal,
          subtotal_amount: totals.subtotalAfter,
          total_amount: totals.grandTotal,
          discount_amount: totals.discount,
          subtotal_before_discount: totals.subtotalBefore,
          bottle_deposit_amount: totals.bottleDepositTotal,
        };

        // Try to POST sale (non-blocking for UX)
        try {
          if (items?.length) await postSale(payload);
        } catch (e: any) {
          console.error('[Payment] Sale POST failed:', e?.message || e);
        }

        // ✅ Build receipt payload to render ReceiptInterface accurately
        const receiptPayload: ReceiptPayload = {
          receiptNumber: makeReceiptNumber(),
          transactionNumber: payload.transaction_number,
          posId: String(payload.pos_id),
          locationId: Number(payload.location_id),
          channel: 'POS',
          paymentMethod: String(payload.payment_method),
          createdAtIso: new Date().toISOString(),
          items: (cartItems || [])
            .map((ci) => {
              const sku = ci.barcode?.trim() || ci.isbn?.trim() || '';
              if (!sku) return null;

              const qty = Math.max(1, ci.quantity || 1);
              const pr = pricingByUpc.get(sku);

              return {
                item_sku: sku,
                name: String(ci.name || sku),
                qty,
                unit_price: Number(ci.price || 0),
                line_subtotal: Number(pr?.line_subtotal ?? (Number(ci.price || 0) * qty)),
                line_discount: Number(pr?.line_discount ?? 0),
                applied_promos: Array.isArray(pr?.applied_promos) ? pr!.applied_promos : [],
              };
            })
            .filter(Boolean) as ReceiptPayload['items'],
          totals: {
            subtotalBefore: totals.subtotalBefore,
            discount: totals.discount,
            subtotalAfter: totals.subtotalAfter,
            bottleDepositTotal: totals.bottleDepositTotal,
            taxTotal: totals.taxTotal,
            grandTotal: totals.grandTotal,
          },
          appliedPromos,
        };

        // ✅ IMPORTANT: now send receipt back to App
        onPaymentComplete(receiptPayload);
      } else {
        setError('Payment processing failed. Please try again or contact support.');
      }
    } catch {
      setError('An error occurred during payment processing. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderPaymentForm = () => {
    switch (paymentMethod) {
      case 'card':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Card Number"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              placeholder="1234 5678 9012 3456"
              fullWidth
              inputProps={{ maxLength: 19 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Expiry Date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                placeholder="MM/YY"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 5 }}
              />
              <TextField
                label="CVV"
                value={cvv}
                onChange={(e) => setCvv(e.target.value)}
                placeholder="123"
                sx={{ flex: 1 }}
                inputProps={{ maxLength: 4 }}
                type="password"
              />
            </Box>
          </Box>
        );
      case 'debit':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Debit Card Number"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              placeholder="1234 5678 9012 3456"
              fullWidth
              inputProps={{ maxLength: 19 }}
            />
            <TextField
              label="PIN"
              value={cvv}
              onChange={(e) => setCvv(e.target.value)}
              placeholder="Enter PIN"
              fullWidth
              type="password"
              inputProps={{ maxLength: 4 }}
            />
          </Box>
        );
      case 'student_card':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Student ID"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              placeholder="Enter your student ID"
              fullWidth
            />
            <Alert severity="info">Payment will be charged to your student account.</Alert>
          </Box>
        );
      case 'gift_card':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Gift Card Number"
              value={giftCardNumber}
              onChange={(e) => setGiftCardNumber(e.target.value)}
              placeholder="Enter gift card number"
              fullWidth
            />
            <TextField
              label="PIN"
              value={giftCardPin}
              onChange={(e) => setGiftCardPin(e.target.value)}
              placeholder="Enter PIN"
              fullWidth
              type="password"
            />
          </Box>
        );
      case 'cash':
        return <Alert severity="info">Please insert cash into the payment slot.</Alert>;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          onClick={onBackToCart}
          startIcon={<ArrowBack />}
          size="large"
          sx={{
            minWidth: 150,
            backgroundColor: '#2C507A',
            color: '#FFFFFF',
            '&:hover': { backgroundColor: '#1E3A5F' },
          }}
        >
          Back to Cart
        </Button>
        <Typography variant="h3" sx={{ flexGrow: 1, color: '#2C507A', fontWeight: 700 }}>
          Payment
        </Typography>
      </Box>

      {pricingError && <Alert severity="warning">{pricingError}</Alert>}

      <Card sx={{ flex: 1, overflow: 'visible' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
          <Box sx={{ flex: 1, overflow: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Payment Method Selection */}
            <Card>
              <CardContent>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#2C507A',
                    fontWeight: 600,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Select Payment Method
                </Typography>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}>
                    <FormControlLabel
                      value="card"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CreditCard />
                          <Typography>Credit Card</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="debit"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccountBalance />
                          <Typography>Debit Card</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="student_card"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QrCode />
                          <Typography>Student Card</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="gift_card"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalAtm />
                          <Typography>Gift Card</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="cash"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalAtm />
                          <Typography>Cash</Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>

            {/* Payment Form */}
            <Card>
              <CardContent>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#2C507A',
                    fontWeight: 600,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Payment Details
                </Typography>
                {renderPaymentForm()}
              </CardContent>
            </Card>

            {/* Promo Form */}
            <Card>
              <CardContent>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#2C507A',
                    fontWeight: 600,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Promo Code
                </Typography>

                {appliedPromos.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {appliedPromos.map((p) => (
                      <Chip key={p} label={p} size="small" color="success" variant="outlined" />
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="e.g. CART5_OVER50"
                    fullWidth
                  />

                  <Button
                    variant="contained"
                    onClick={handleApplyPromo}
                    disabled={promoLoading}
                    sx={{ backgroundColor: '#2C507A', '&:hover': { backgroundColor: '#1E3A5F' } }}
                  >
                    {promoLoading ? 'Applying...' : 'Apply Promo'}
                  </Button>

                  {promoError && <Alert severity="error">{promoError}</Alert>}
                  {promoSuccess && <Alert severity="success">{promoSuccess}</Alert>}
                </Box>
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card>
              <CardContent>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#2C507A',
                    fontWeight: 600,
                    mb: 2,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Order Summary
                </Typography>

                <Divider sx={{ my: 2, borderColor: '#E9ECEF' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ color: '#6C757D' }}>Subtotal:</Typography>
                  <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>{formatPrice(totals.subtotalBefore)}</Typography>
                </Box>

                {totals.discount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: '#6C757D' }}>Discount:</Typography>
                    <Typography sx={{ color: '#28a745', fontWeight: 600 }}>-{formatPrice(totals.discount)}</Typography>
                  </Box>
                )}

                {totals.bottleDepositTotal > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: '#6C757D' }}>Bottle Deposit:</Typography>
                    <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>{formatPrice(totals.bottleDepositTotal)}</Typography>
                  </Box>
                )}

                {totals.taxTotal > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ color: '#6C757D' }}>Tax:</Typography>
                    <Typography sx={{ color: '#2C507A', fontWeight: 600 }}>{formatPrice(totals.taxTotal)}</Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2, borderColor: '#E9ECEF' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h5" sx={{ color: '#2C507A', fontWeight: 700 }}>
                    Total:
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#2C507A', fontWeight: 700 }}>
                    {formatPrice(totals.grandTotal)}
                  </Typography>
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          onClick={onBackToCart}
          size="large"
          sx={{
            minWidth: 150,
            backgroundColor: '#2C507A',
            color: '#FFFFFF',
            borderColor: '#2C507A',
            '&:hover': { backgroundColor: '#1E3A5F', borderColor: '#1E3A5F' },
          }}
        >
          Back to Cart
        </Button>

        <Button
          variant="contained"
          onClick={handlePayment}
          disabled={isProcessing}
          size="large"
          startIcon={isProcessing ? <CircularProgress size={20} /> : <Payment />}
          sx={{
            minWidth: 200,
            backgroundColor: '#2C507A',
            '&:hover': { backgroundColor: '#1E3A5F' },
            '&:disabled': { backgroundColor: '#ADB5BD' },
          }}
        >
          {isProcessing ? 'Processing Payment...' : `Pay ${formatPrice(totals.grandTotal)}`}
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentInterface;